import "./prism-retrieval.css"

import { Fragment } from "react"

import type { PrismStep } from "./prism-retrieval-constants"

import { Circle, CircleCheckSolid, CircleDot } from "@/components/icons"
import { NodeConnector, NodePill } from "@/components/ui/node-pill"
import { cn } from "@/lib/utils"

type StepStatus = "pending" | "active" | "done"

const STATUS_ICON = { pending: Circle, active: CircleDot, done: CircleCheckSolid } as const

type PrismStepPillsProps = {
  steps: readonly PrismStep[]
  /** Steps completed, from the left. */
  done: number
  /** Index of the step in progress, or -1 when none is. */
  active: number
  label: string
  connectorLength?: number
  className?: string
}

/**
 * A Prism step track: node pills joined by connectors (DESIGN.md
 * "Micrographics"). Pending steps are quiet, the step in progress is blue,
 * done steps carry a check. State changes move on Guiding, which the node
 * pill and connector styles already apply.
 */
export function PrismStepPills({
  steps,
  done,
  active,
  label,
  connectorLength = 2,
  className,
}: PrismStepPillsProps) {
  return (
    <ol aria-label={label} className={cn("flex flex-wrap items-center gap-y-1.5", className)}>
      {steps.map((step, index) => {
        let status: StepStatus = "pending"
        if (index < done) status = "done"
        else if (index === active) status = "active"
        const StatusIcon = STATUS_ICON[status]
        return (
          <Fragment key={step.key}>
            {index > 0 && (
              <NodeConnector
                className={cn("mx-1", index <= done ? "text-foreground" : "text-border")}
                length={connectorLength}
              />
            )}
            <li
              aria-current={status === "active" ? "step" : undefined}
              data-state={status}
              data-step={step.key}
            >
              <NodePill
                size="sm"
                className={cn(
                  status === "pending" && "border-border text-muted-foreground",
                  status === "active" && "border-foreground bg-primary text-primary-foreground"
                )}
              >
                {/* Rendered here, not through `icon`: NodePill sets fill="none" on an
                    inactive icon, which blanks the solid check. */}
                <StatusIcon aria-hidden="true" className="node-pill__icon" />
                {/* Holds the widest label this step can take from mount, so a
                    step changing its label never pushes the steps after it. */}
                <span className="prism-label" data-label={step.reserve ?? step.label}>
                  {step.label}
                </span>
              </NodePill>
            </li>
          </Fragment>
        )
      })}
    </ol>
  )
}
